/* eslint-disable @typescript-eslint/no-unused-vars */
import { FC, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button } from '@progress/kendo-react-buttons'

import { SearchBody } from '../../../components/layouts'
import { SearchCondition } from './SearchCondition'
import { SimulationBuildingSearchResultList } from './SimulationBuildingSearchResultList'
import { useAccessibleAuthority, usePageTitle } from '../../../components/hooks'
import { SimulationBuildingListGateway } from '../../../gateway/simulation/SimulationBuildingListGateway'
import {
  SimulationBuildingSearchActions,
  CommonActions,
  useAppDispatch,
  useAppSelector
} from '../../../stores'
import { ErrorMessage, ErrorMessages } from '../../../components/parts'
import { MessageProvider } from '../../../services'
import { BuildingInfoExpense, GetSimulationBuildingListReq, GetSimulationBuildingListRes } from '@gateway.Simulation'
import { Authority } from '../../../gateway/system/Authority'

export const systemTime = () => {
  const month = new Date().getMonth() + 1
  const year = new Date().getFullYear()
  const date = month > 9 ? `${year}${month}` : `${year}0${month}`
  return date
}

/** 検索条件 初期表示 */
export const searchInfo = {
  simulationYm: systemTime(),
  procurementNo: '',
  expense: '',
  expenseNotApplicableFlg: [],
  updSts: [],
  opportunityProgressKbn: [],
  evaluationValue: [],
  branch: [],
  constructionNm: '',
  scheduleFrom: null,
  scheduleTo: null,
  constructionCd: [],
  chief: '',
  person: '',
  supplierNm: ''
}

export const SimulationBuildingSearch: FC = () => {
  useAccessibleAuthority(Authority.Builder, Authority.Material, Authority.Branch)
  const beforePageTitle = usePageTitle('山積対象物件一覧')
  const navigate = useNavigate()
  const location = useLocation()
  const userProp = useAppSelector((state) => state.common.userProp)
  const simulationBuildingList = useAppSelector(
    (state) => state.simulationbuildingsearch.simulationBuildingList
  )
  const simulationBuildingSearch = useAppSelector(
    (state) => state.simulationbuildingsearch.simulationBuildingSearch
  )
  const status = useAppSelector(
    (state) => state.simulationbuildingsearch.status
  )
  const priorityStatus = useAppSelector(
    (state) => state.simulationbuildingsearch.priorityStatus
  )
  const message = useAppSelector(
    (state) => state.simulationbuildingsearch.messageInfo
  )
  const skip = useAppSelector(
    (state) => state.simulationbuildingsearch.skip
  )
  const [simulationBuildingListState, setSimulationBuildingListState] = useState<BuildingInfoExpense[]>([])
  const dispatch = useAppDispatch()
  const [notificationMessages, setNotificationMessages] = useState<ErrorMessage[]>([])
  const [errorMessages, setErrorMessages] = useState<ErrorMessage[]>([])
  const [infoMessages, setInfoMessages] = useState<ErrorMessage[]>([])

  // 親物件の件数
  const simulationBuildingListLength = simulationBuildingList
    .filter((item) => item.subNo === '00' && item.expenseSeq === '00')
    .length

  useEffect(() => {
    if (userProp !== undefined) {
      // 検索結果のストアを空にする
      dispatch(SimulationBuildingSearchActions.setSimulationBuildingList([]))
      let condition: GetSimulationBuildingListReq = searchInfo
      if (beforePageTitle !== '山積計算') {
        if (userProp.authority === Authority.Branch) {
          condition = { ...searchInfo, branch: [userProp.branchCode] }
        }
        // 検索条件を明示的に初期状態に設定する
        dispatch(
          SimulationBuildingSearchActions.setSimulationBuildingSearch(condition)
        )
        // スキップ明細数を初期状態に設定する
        dispatch(SimulationBuildingSearchActions.setSkip(0))
        // ターゲットを初期状態に設定する
        dispatch(SimulationBuildingSearchActions.setTarget(''))
      } else {
        // 労務山積計算画面で確定後に遷移してきた場合、完了Notificationを表示する
        if (status) {
          setNotificationMessages([{
            id: 'I004',
            content: MessageProvider.provide(
              'I004',
              ...['確定']
            )
          }])
        // 労務山積計算画面で優先度切替後に遷移してきた場合、受付Notificationを表示する
        } else if (priorityStatus) {
          setNotificationMessages([{
            id: 'I017',
            content: MessageProvider.provide(
              'I017',
              ...['自動山積計算']
            )
          }])
        }
        // 労務山積計算画面から返却されたメッセージが存在する場合、ダイアログを表示する
        if (Array.isArray(message) && message.length > 0) {
          const messages = message.map<ErrorMessage>((message: any) => {
            return {
              id: message.id,
              content: message.content
            }
          })
          setInfoMessages(messages)
        }
        condition = simulationBuildingSearch
        // 労務山積計算画面から遷移してきた場合は、ストアに保持している検索条件を設定する
        dispatch(
          SimulationBuildingSearchActions.setSimulationBuildingSearch(condition)
        )
      }
      apiSimulationBuildingGateway(condition)
    }
  }, [dispatch, userProp])

  useEffect(() => {
    const tempSkip = (beforePageTitle === '山積計算' || beforePageTitle === '山積対象物件一覧') ? skip : 0
    setBuildingInfoList({ buildingInfoList: simulationBuildingList }, tempSkip)
  }, [simulationBuildingList])

  const apiSimulationBuildingGateway = (searchCondition: GetSimulationBuildingListReq) => {
    dispatch(CommonActions.setIsLoading(true))
    dispatch(SimulationBuildingSearchActions.setStatus(false))
    dispatch(SimulationBuildingSearchActions.setMessageInfo([]))
    SimulationBuildingListGateway.getSimulationBuildingList(
      searchCondition
    )
      .then((res) => {
        if (res.error) {
          const resMessages = res.messages
          if (Array.isArray(resMessages) && resMessages.length > 0) {
            const messages = resMessages.map<ErrorMessage>((message: any) => {
              return {
                id: message.id,
                content: message.message
              }
            })
            setErrorMessages(messages)
          }
        } else {
          // 検索結果存在チェック
          if (res.data.buildingInfoList.length === 0) {
            setNotificationMessages([{
              id: 'I001',
              content: MessageProvider.provide(
                'I001',
                ...[]
              )
            }])
            dispatch(
              SimulationBuildingSearchActions.setSimulationBuildingList(res.data.buildingInfoList)
            )
          } else if (res.data.buildingInfoList.length > 1000) {
            // 1000件以上の場合
            setNotificationMessages([{
              id: 'I015',
              content: MessageProvider.provide(
                'I015',
                ...['1000']
              )
            }])
            dispatch(
              SimulationBuildingSearchActions.setSimulationBuildingList(
                res.data.buildingInfoList.slice(0, 1000)
              )
            )
          } else {
            // 1~1000件以内の場合
            dispatch(
              SimulationBuildingSearchActions.setSimulationBuildingList(res.data.buildingInfoList)
            )
          }
        }
      })
      .finally(() => {
        dispatch(CommonActions.setIsLoading(false))
      })
  }

  /* 検索 */
  const handleOnSearchBtnClick = () => {
    dispatch(SimulationBuildingSearchActions.setSkip(0))
    dispatch(SimulationBuildingSearchActions.setTarget(''))
    apiSimulationBuildingGateway(simulationBuildingSearch)
  }

  /** クリア */
  const handleOnClearBtnClick = () => {
    if (userProp !== undefined && userProp.authority === Authority.Branch) {
      const condition = { ...searchInfo, branch: [userProp.branchCode] }
      // 検索条件を明示的に初期状態に設定する
      dispatch(SimulationBuildingSearchActions.setSimulationBuildingSearch(condition))
      return
    }
    dispatch(
      SimulationBuildingSearchActions.setSimulationBuildingSearch(searchInfo)
    )
  }

  /**
   * 戻るボタン
   */
  const handleReturn = () => {
    navigate(-1)
    dispatch(SimulationBuildingSearchActions.setSimulationBuildingList([]))
  }

  const setBuildingInfoList = (params: GetSimulationBuildingListRes, skip: number, propsTake?: number) => {
    let preProNo: string | null = ''
    let curTake = 0
    let skipFlg = true
    let skipProcurementNo: string | null = ''

    // 指定されたページの調達番号の先頭
    if (skip !== 0 && params.buildingInfoList.length !== 0) {
      skipProcurementNo = params.buildingInfoList
        .filter((v) => v.subNo === '00' && v.expenseSeq === '00')[skip]
        .procurementNo
    }

    const buildingInfoList = params.buildingInfoList.map((buildingInfo) => {
      // 先頭の調達番号までスキップ
      if (
        skip !== 0 &&
        skipProcurementNo !== buildingInfo.procurementNo &&
        skipFlg
      ) {
        return null
      }

      skipFlg = false

      // 調達番号が同じ物件情報の枝番（子）が00の物件情報を親、01~99の物件情報を子として扱う
      if (preProNo === buildingInfo.procurementNo) {
        if (buildingInfo.subNo === '00' && buildingInfo.expenseSeq === '00') {
          curTake++
        }
        preProNo = buildingInfo.procurementNo
        return buildingInfo
      } else if (curTake < 20) {
        if (buildingInfo.subNo === '00' && buildingInfo.expenseSeq === '00') {
          curTake++
        }
        preProNo = buildingInfo.procurementNo
        return buildingInfo
      } else {
        preProNo = ''
        return null
      }
    }).filter(v => v) as BuildingInfoExpense[]

    setSimulationBuildingListState(buildingInfoList)

    dispatch(SimulationBuildingSearchActions.setSkip(skip))
  }

  const setSkipBuilding = (skip: number) => {
    setBuildingInfoList({ buildingInfoList: simulationBuildingList }, skip)
  }

  return (
    <>
      <SearchBody
        total={simulationBuildingListLength}
        changeSkipBuilding={(skip: number) => setSkipBuilding(skip)}
        skip={skip}
        leftPane={<SearchCondition />}
        leftPaneButton={
          <>
            <Button onClick={handleOnClearBtnClick}>クリア</Button>
            <Button themeColor="primary" onClick={handleOnSearchBtnClick}>
              検索
            </Button>
          </>
        }
        rightPane={
          <SimulationBuildingSearchResultList
            skip={skip}
            apiSimulationBuildingGateway={apiSimulationBuildingGateway}
            setNotificationMessages={setNotificationMessages}
            setErrorMessages={setErrorMessages}
            buildingList={simulationBuildingListState}
            setSimulationBuildingListState={setSimulationBuildingListState}
          />
        }
        rightPaneButton={<Button onClick={handleReturn}>戻る</Button>}
      />
      <ErrorMessages type="notification" messages={notificationMessages}/>
      <ErrorMessages type="dialog" messages={errorMessages} />
      <ErrorMessages type="dialog" messages={infoMessages} title='インフォメーション' />
    </>
  )
}
